<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { TaskData } from '../lib/tasks';
  import CropImage from './CropImage.svelte';
  import CaseNumberCard from './MatterCard/CaseNumberCard.svelte';
  import TaskList from './MatterCard/TaskList.svelte';
  import ProgressRefresh from './ProgressRefresh.svelte';

  let {
    title,
    badges,
    case_number,
    stage,
    vrm,
    vehicle_image_url,
    before_tasks,
    tasks,
    ts = null,
    children
  } : {
    title : string;
    badges : {label : string; value : string; mobile : boolean}[];
    case_number? : string;
    stage? : string;
    vrm? : string;
    vehicle_image_url? : string;
    before_tasks? : Snippet;
    tasks : TaskData[];
    ts? : number;
    children : Snippet;
  } = $props()

  const NON_BREAKING_HYPHEN = "‑"
  const NON_BREAKING_SPACE = " "
  function formatTitle(title: string) {
    return title
      .replace(/-/g, NON_BREAKING_HYPHEN) // Don't wrap on "-" as in "CR-V"
      .replace(/ (?=\S{1,7}$)/, NON_BREAKING_SPACE) // Prevent orphans less than 8 chars long
  }

  function formatVrm(vrm: string) {
    // Standard GB VRM format since 2001. Has no effect if vrm doesn't match the pattern.
    // https://en.wikipedia.org/wiki/Vehicle_registration_plates_of_the_United_Kingdom#Great_Britain
    return vrm.replace(/^([A-Z]{2}[0-9]{2})([A-Z]{3})$/, "$1 $2")
  }
</script>

<CaseNumberCard {case_number} {stage}>
  <div class="rounded bg-white p-4 lg:p-6 flex flex-col gap-2 relative">
    <div class="overflow-visible">
      <div class="flex flex-row-reverse items-center justify-between {vehicle_image_url ? "-mt-2" : "-mt-1"} lg:mt-0 lg:contents">
        {#if vehicle_image_url}
          <div class="relative float-right w-40 lg:w-48 h-24 lg:-mt-2 shrink-0">
            <CropImage src={vehicle_image_url} class="w-full h-full" />
            {#if vrm}
              <div class="absolute bottom-2 sm:bottom-1.5 -right-2 sm:right-0 whitespace-nowrap rounded-[3px] border border-black/60 bg-[#F7CE46] px-1.5 text-xs font-bold tracking-wider text-black shadow-sm">
                {formatVrm(vrm)}
              </div>
            {/if}
          </div>
        {/if}
        <h2 class="grow ui-section-title lg:text-2xl lg:mb-2 !leading-[1.625rem] py-[1px] text-ellipsis {vehicle_image_url ? "line-clamp-3" : "line-clamp-2"}">
          {formatTitle(title)}
        </h2>
      </div>
      {#if badges.length}<div class="hidden lg:block">{@render badgesList()}</div>{/if}
    </div>
    {#if before_tasks}{@render before_tasks()}{/if}
    {#if children}
      {@render children()}
    {:else}
      <TaskList {tasks} />
    {/if}
    {#if badges.length}
      <div class="lg:hidden grid-cols-[1fr_max-content] grid items-center -mb-1 -mx-2">
        {@render badgesList({mobile: true})}
      </div>
    {/if}
    {#key ts}<ProgressRefresh noun="claim" channel={case_number} {ts} class="[body:has(dialog[open])_&]:hidden" />{/key}
  </div>
</CaseNumberCard>

{#snippet badgesList({mobile} = {mobile: false})}
  <div class={`flex flex-wrap gap-1 ${mobile ? "min-w-0 pt-2 pb-1" : ""}`}>
    {#each badges as badge (badge.label)}
      {#if mobile || !badge.mobile}
        <span class="badge badge-ghost inline-block whitespace-nowrap overflow-hidden text-nowrap text-ellipsis max-w-full">
          {badge.label}: {badge.value}
        </span>
      {/if}
    {/each}
  </div>
{/snippet}
